(() => {
  const elementName = 'manatorsk-product-relation';

  if (customElements.get(elementName)) return;

  class ManatorskProductRelation extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.loadingElement = this.querySelector('[data-loading]');
      this.listElement = this.querySelector('[data-list]');
      this.toggleElement = this.querySelector('[data-toggle]');
      this.toggleLabelElement = this.querySelector('[data-toggle-label]');
      this.messageElement = this.querySelector('[data-message]');
      this.abortController = new AbortController();

      try {
        const configElement = this.querySelector('[data-relation-config]');
        this.config = JSON.parse(configElement?.textContent || '{}');
      } catch (error) {
        console.error('Manatorsk relations: invalid configuration.', error);
        this.hideRelation();
        return;
      }

      this.toggleElement?.addEventListener('click', () => this.toggleProducts());
      this.listElement?.addEventListener('click', (event) => this.handleAction(event));

      this.loadProducts();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    parseIds(value) {
      const values = Array.isArray(value) ? value : [value];
      const ids = [];

      values.forEach((entry) => {
        if (!entry) return;

        if (typeof entry === 'object') {
          const objectId = entry.admin_graphql_api_id || entry.id;
          if (typeof objectId === 'string') ids.push(objectId);
          return;
        }

        const matches = String(entry).match(/gid:\/\/shopify\/Product\/\d+/g);
        if (matches) ids.push(...matches);
      });

      return [...new Set(ids)];
    }

    async loadProducts() {
      const ids = this.parseIds(this.config.ids);

      if (!ids.length) {
        this.hidden = true;
        return;
      }

      const requests = ids.map((id) => this.fetchProduct(id));
      const results = await Promise.allSettled(requests);
      const products = results
        .filter((result) => result.status === 'fulfilled' && result.value)
        .map((result) => result.value);
      const failures = results.filter((result) => result.status === 'rejected');

      if (!products.length) {
        if (failures.length) {
          console.error('Manatorsk relations: products could not be loaded.', failures);
        }

        this.hideRelation();
        return;
      }

      products.sort((a, b) => Number(this.isSellable(b)) - Number(this.isSellable(a)));
      this.renderProducts(products);

      if (failures.length) {
        console.warn('Manatorsk relations: one or more products could not be loaded.', failures);
      }
    }

    async fetchProduct(id) {
      const endpoint = String(this.config.endpoint || '');
      const response = await fetch(`${endpoint}${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        mode: 'cors',
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Product request failed with status ${response.status}.`);
      }

      const product = await response.json();

      if (!this.isUsableProduct(product)) {
        return null;
      }

      // Elastic innehåller SEK. Shopify returnerar marknadens faktiska pris.
      product.relationPresentmentPrice = null;
      const productUrl = this.getProductUrl(product);
      if (productUrl) {
        try {
          const url = new URL(productUrl, window.location.origin);
          url.pathname += '.js';
          url.search = '';
          url.hash = '';
          const localResponse = await fetch(url.pathname, {
            headers: { Accept: 'application/json' },
            signal: this.abortController.signal,
          });
          if (!localResponse.ok) throw new Error(`Local product request failed: ${localResponse.status}.`);
          const localProduct = await localResponse.json();
          const variantId = String(this.getVariantId(product)).split('/').pop();
          const localVariant = (localProduct.variants || []).find(
            (variant) => String(variant.id) === variantId,
          );
          const localPrice = variantId ? localVariant?.price : localProduct.price;
          if (localPrice !== null && localPrice !== undefined && localPrice !== '' && Number.isFinite(Number(localPrice))) {
            product.relationPresentmentPrice = Number(localPrice) / 100;
          }
        } catch (error) {
          if (error.name === 'AbortError') throw error;
          // Visa rekommendationen utan pris hellre än ett felaktigt valutabelopp.
          console.warn('Manatorsk relations: local price could not be loaded.', error);
        }
      }
      return { ...product, relationGid: id };
    }

    isUsableProduct(product) {
      if (!product || typeof product !== 'object') return false;

      const status = String(product.status || '').toUpperCase();
      if (status && status !== 'ACTIVE') return false;

      return Boolean(product.title && (product.handle || product.onlineStoreUrl || product.url));
    }

    normalizeStatuses(value) {
      if (Array.isArray(value)) {
        return value.map((status) => String(status).toLowerCase());
      }

      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed.map((status) => String(status).toLowerCase());
          }
        } catch (_error) {
          // Some older index entries contain a plain string instead of JSON.
        }

        return [value.toLowerCase()];
      }

      return [];
    }

    getProductState(product) {
      const statuses = this.normalizeStatuses(product.preorder);
      const inventory = Number(product.inventoryQuantity || 0);
      const isPreorder = statuses.some((status) => status.includes('preorder'));
      const isComingSoon = statuses.some((status) => status.includes('comingsoon'));
      const isStoreOnly = statuses.some((status) => status.includes('storeonly'));

      return {
        inventory,
        isPreorder,
        isComingSoon,
        isStoreOnly,
        isSellable: inventory > 0 && !isComingSoon && !isStoreOnly,
      };
    }

    isSellable(product) {
      return this.getProductState(product).isSellable;
    }

    getVariant(product) {
      const variants = Array.isArray(product.variants) ? product.variants : [];

      return (
        variants.find(
          (variant) =>
            variant.available === true ||
            variant.availableForSale === true ||
            Number(variant.inventoryQuantity || 0) > 0,
        ) || variants[0] || null
      );
    }

    getVariantId(product) {
      const variant = this.getVariant(product);
      return variant?.admin_graphql_api_id || variant?.id || '';
    }

    getProductUrl(product) {
      const root = window.Shopify?.routes?.root || '/';
      const prefix = `/${String(root).replace(/^\/+|\/+$/g, '')}`.replace(/\/$/, '') + '/';
      if (product.handle) return `${prefix}products/${encodeURIComponent(product.handle)}`;

      // Återanvänd produktdelen, aldrig Elastic-datans domän eller gamla språkprefix.
      for (const value of [product.onlineStoreUrl, product.url]) {
        if (!value) continue;
        try {
          const url = new URL(String(value), window.location.origin);
          if (!['http:', 'https:'].includes(url.protocol)) continue;
          const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
          if (match) return `${prefix}products/${match[1]}${url.search}${url.hash}`;
        } catch (_error) {
          // Försök nästa URL om indexets värde är ogiltigt.
        }
      }
      return '';
    }

    getReleaseDate(product) {
      let schedule = product.preorder_schedule;

      if (typeof schedule === 'string') {
        try {
          schedule = JSON.parse(schedule);
        } catch (_error) {
          return '';
        }
      }

      return schedule?.release_date || '';
    }

    renderProducts(products) {
      this.listElement.replaceChildren();

      products.forEach((product, index) => {
        const card = this.createProductCard(product);
        if (index >= 3) card.hidden = true;
        this.listElement.append(card);
      });

      this.loadingElement.hidden = true;
      this.listElement.hidden = false;
      this.hidden = false;
      this.style.removeProperty('display');

      if (products.length > 3) {
        this.toggleElement.hidden = false;
      }
    }

    createProductCard(product) {
      const labels = this.config.labels || {};
      const state = this.getProductState(product);
      const variantId = this.getVariantId(product);
      const productUrl = this.getProductUrl(product);
      const title = String(product.title || 'Produkt');

      const card = document.createElement('article');
      card.className = 'manatorsk-relation__card';
      card.dataset.inventory = String(state.inventory);

      card.append(this.createImage(product, title, productUrl));

      const content = document.createElement('div');
      content.className = 'manatorsk-relation__content';

      const heading = document.createElement('h3');
      heading.className = 'manatorsk-relation__name';

      if (productUrl) {
        const link = document.createElement('a');
        link.href = productUrl;
        link.textContent = title;
        heading.append(link);
      } else {
        heading.textContent = title;
      }

      content.append(heading);

      const meta = document.createElement('div');
      meta.className = 'manatorsk-relation__meta';

      if (product.relationPresentmentPrice !== null && product.relationPresentmentPrice !== undefined) {
        const price = document.createElement('span');
        price.className = 'manatorsk-relation__price';
        price.textContent = this.formatPrice(product.relationPresentmentPrice);
        meta.append(price);
      }

      const status = this.createStatus(state, labels);
      if (status) meta.append(status);

      content.append(meta);
      card.append(content);

      const action = document.createElement('button');
      action.type = 'button';
      action.dataset.productTitle = title;
      action.dataset.productUrl = productUrl;
      action.dataset.variantId = variantId;

      if (state.isSellable && variantId) {
        action.className = 'manatorsk-relation__action manatorsk-relation__action--add';
        action.dataset.relationAction = 'add';
        action.dataset.preorder = String(state.isPreorder);
        action.dataset.releaseDate = this.getReleaseDate(product);
        action.setAttribute('aria-label', `${labels.addToCart || 'Lägg till i varukorg'}: ${title}`);
        action.title = labels.addToCart || 'Lägg till i varukorg';
        action.innerHTML = this.plusIcon();
      } else {
        action.className = 'manatorsk-relation__action manatorsk-relation__action--notify';
        action.dataset.relationAction = 'notify';
        action.setAttribute('aria-label', `${labels.notify || 'Bevaka'}: ${title}`);
        action.title = labels.notify || 'Bevaka';
        action.innerHTML = this.watchIcon();
      }

      card.append(action);
      return card;
    }

    createImage(product, title, productUrl) {
      const wrapper = document.createElement(productUrl ? 'a' : 'div');
      wrapper.className = product.imgUrl
        ? 'manatorsk-relation__image-link'
        : 'manatorsk-relation__image-placeholder';

      if (productUrl && wrapper instanceof HTMLAnchorElement) {
        wrapper.href = productUrl;
        wrapper.setAttribute('aria-label', title);
      }

      if (product.imgUrl) {
        const image = document.createElement('img');
        image.className = 'manatorsk-relation__image';
        image.src = String(product.imgUrl);
        image.alt = title;
        image.width = 68;
        image.height = 68;
        image.loading = 'lazy';
        wrapper.append(image);
      } else {
        wrapper.innerHTML = this.imagePlaceholderIcon();
      }

      return wrapper;
    }

    createStatus(state, labels) {
      let type = '';
      let text = '';

      if (state.isPreorder) {
        type = 'preorder';
        text = labels.preorder || 'Förbeställning';
      } else if (state.isComingSoon) {
        type = 'comingsoon';
        text = labels.comingsoon || 'Kommande';
      } else if (state.isStoreOnly) {
        type = 'storeonly';
        text = labels.storeonly || 'Endast i butik';
      } else if (state.inventory <= 0) {
        type = 'soldout';
        text = labels.soldOut || 'Slutsåld';
      }

      if (!type) return null;

      const status = document.createElement('span');
      status.className = `manatorsk-relation__status manatorsk-relation__status--${type}`;
      status.textContent = text;
      return status;
    }

    async handleAction(event) {
      const button = event.target.closest('[data-relation-action]');
      if (!button || !this.contains(button)) return;

      event.preventDefault();

      if (button.dataset.relationAction === 'add') {
        await this.addToCart(button);
      } else if (button.dataset.relationAction === 'notify') {
        this.requestNotification(button);
      }
    }

    async addToCart(button) {
      const labels = this.config.labels || {};
      const variantId = button.dataset.variantId;
      if (!variantId) {
        this.showMessage(labels.loadError || 'Variant saknas.', 'error');
        return;
      }

      const originalContent = button.innerHTML;
      button.disabled = true;
      button.innerHTML = '<span class="manatorsk-relation__spinner" aria-hidden="true"></span>';

      const attributes = [];
      if (button.dataset.preorder === 'true') {
        attributes.push({ key: 'Typ', value: 'Förbeställning' });

        if (button.dataset.releaseDate) {
          attributes.push({ key: 'Release', value: button.dataset.releaseDate });
        }
      }

      try {
        const result = await this.updateCart(variantId, attributes);

        if (result?.warnings?.length) {
          this.showMessage(result.warnings[0].message, 'warning');
        }

        button.innerHTML = this.checkIcon();
        window.setTimeout(() => {
          button.innerHTML = originalContent;
          button.disabled = false;
        }, 1100);
      } catch (error) {
        console.error('Manatorsk relations: add to cart failed.', error);
        this.showMessage(error.message || 'Kunde inte lägga produkten i varukorgen.', 'error');
        button.innerHTML = originalContent;
        button.disabled = false;
      }
    }

    async updateCart(variantId, attributes) {
      const properties = Object.fromEntries(attributes.map(({ key, value }) => [key, value]));
      const rawVariantId = String(variantId).split('/').pop();
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: rawVariantId, quantity: 1, properties }],
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Kunde inte lägga produkten i varukorgen.');
      }

      await this.refreshCartCount();
      return { warnings: [] };
    }

    async refreshCartCount() {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();
      const itemCount = Number(cart.item_count || 0);

      document
        .querySelectorAll(
          '.cart-count, .site-header__cart-count, .cart-link__bubble-num, [data-cart-count], [data-cart-item-count]',
        )
        .forEach((element) => {
          element.textContent = String(itemCount);
          element.hidden = itemCount === 0;
        });

      document.querySelectorAll('.cart-link__bubble').forEach((bubble) => {
        bubble.classList.toggle('cart-link__bubble--visible', itemCount > 0);
      });

      document.dispatchEvent(
        new CustomEvent('manatorsk:cart-updated', {
          bubbles: true,
          detail: { cart },
        }),
      );
    }

    requestNotification(button) {
      const detail = {
        variantId: button.dataset.variantId,
        productTitle: button.dataset.productTitle,
        productUrl: button.dataset.productUrl,
      };

      const event = new CustomEvent('manatorsk:notify-request', {
        bubbles: true,
        cancelable: true,
        detail,
      });

      document.dispatchEvent(event);

      if (event.defaultPrevented) return;

      if (window.ManatorskNotify?.open) {
        window.ManatorskNotify.open(detail);
        return;
      }

      if (detail.productUrl) {
        window.location.assign(detail.productUrl);
      } else {
        this.showMessage(this.config.labels?.notifyUnavailable || 'Bevakning är inte tillgänglig.', 'error');
      }
    }

    toggleProducts() {
      const expanded = this.toggleElement.getAttribute('aria-expanded') === 'true';
      const cards = [...this.listElement.querySelectorAll('.manatorsk-relation__card')];

      cards.forEach((card, index) => {
        if (index >= 3) card.hidden = expanded;
      });

      this.toggleElement.setAttribute('aria-expanded', String(!expanded));
      this.toggleLabelElement.textContent = expanded
        ? this.config.labels?.showMore || 'Visa fler'
        : this.config.labels?.showLess || 'Visa färre';

      if (expanded) {
        this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    formatPrice(value) {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return String(value);

      try {
        return new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'sv-SE', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || this.config.currency || 'SEK',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (_error) {
        return `${amount} ${window.Shopify?.currency?.active || this.config.currency || 'SEK'}`;
      }
    }

    showLoadError() {
      if (this.loadingElement) this.loadingElement.hidden = true;
      this.showMessage(this.config?.labels?.loadError || 'Kunde inte hämta produkterna.', 'error');
    }

    hideRelation() {
      window.clearTimeout(this.messageTimer);

      if (this.loadingElement) this.loadingElement.hidden = true;
      if (this.listElement) {
        this.listElement.replaceChildren();
        this.listElement.hidden = true;
      }
      if (this.toggleElement) this.toggleElement.hidden = true;
      if (this.messageElement) this.messageElement.hidden = true;

      this.hidden = true;
      this.style.setProperty('display', 'none', 'important');
    }

    showMessage(message, type) {
      if (!this.messageElement) return;

      window.clearTimeout(this.messageTimer);
      this.messageElement.textContent = message;
      this.messageElement.className = `manatorsk-relation__message is-${type}`;
      this.messageElement.hidden = false;

      this.messageTimer = window.setTimeout(() => {
        this.messageElement.hidden = true;
      }, type === 'error' ? 5000 : 3000);
    }

    plusIcon() {
      return '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    }

    watchIcon() {
      return '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="12" r="2.8" stroke="currentColor" stroke-width="1.8"/></svg>';
    }

    checkIcon() {
      return '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m5 12 4.5 4.5L19 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }

    imagePlaceholderIcon() {
      return '<svg viewBox="0 0 24 24" width="30" height="30" fill="none" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor"/><circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor"/><path d="m21 15-5-5L5 21" stroke="currentColor"/></svg>';
    }
  }

  customElements.define(elementName, ManatorskProductRelation);
})();
